import { Resource } from '@medplum/fhirtypes'
import {
  IconCalendarEvent,
  IconClipboardPlusFilled,
  IconClipboardTextFilled,
  IconClock,
  IconFileDescriptionFilled,
  IconFileInvoiceFilled,
  IconFolderFilled,
  IconFriends,
  IconHeartbeat,
  IconMedicalCrossFilled,
  IconMicroscopeFilled,
  IconPillFilled,
  IconProps,
  IconShieldFilled,
  IconVaccine,
  IconLicense,
} from '@tabler/icons-react'
import { ProcedureIcon, SocialHistoryIcon } from './icons/Icons.tsx'
import { ChartItem, items } from './PatientChart.utils.tsx'
import { doseSpotMedicationText, isDoseSpotPrescription } from './doseSpotPrescription.ts'
import {
  OVERVIEW_DOC_TYPE_CODE,
  OVERVIEW_DOC_TYPE_SYSTEM,
  RISKS_GAPS_DOC_TYPE_CODE,
} from '@/model/middleware-types'
import { ElementType, ForwardRefExoticComponent } from 'react'

type IconType = ForwardRefExoticComponent<IconProps> | ElementType

type Response = {
  title: string
  icon: IconType
  color: string
  item?: ChartItem
}

/** Type guard to check if a value can be rendered as a React icon component */
export function isRenderableIcon(icon: unknown): icon is IconType {
  if (!icon) return false
  if (typeof icon === 'function') return true
  if (typeof icon === 'object' && icon !== null && '$$typeof' in icon) return true
  return false
}

const AI_REPORT_DOC_TYPE_CODES = [OVERVIEW_DOC_TYPE_CODE, RISKS_GAPS_DOC_TYPE_CODE]

/**
 * Generated Clinical Summary / Risks & Gaps reports are not clinical events. The bot no
 * longer indexes them, but timeline entries written earlier still point at them (PD-1397).
 */
export function isAiReportDocument(resource?: Resource): boolean {
  if (resource?.resourceType !== 'DocumentReference') return false
  return !!resource.type?.coding?.some(
    (coding) =>
      coding.system === OVERVIEW_DOC_TYPE_SYSTEM &&
      AI_REPORT_DOC_TYPE_CODES.includes(coding.code ?? ''),
  )
}

export const extractTitleAndIcon = (resource?: Resource): Response => {
  let icon = IconClock
  let color = '#7D7987'

  if (!resource)
    return {
      title: 'Unknown',
      icon,
      color,
    }

  let title: string = 'Not provided'
  let item: ChartItem | undefined = undefined

  switch (resource.resourceType) {
    case 'DocumentReference': {
      const description = resource.description || ''
      const type = resource.type?.text || resource.type?.coding?.[0]?.display
      title = `${description}${type ? ` (${type})` : ''}`
      if (resource.type?.coding?.find((item) => item.code === 'ProgressNote')) {
        icon = IconFileDescriptionFilled
        color = '#4b20c0'
        item = items.find((item) => item.dataKey === 'progress-notes')
      } else if (resource.type?.coding?.find((item) => item.code === 'HistoryPhysical')) {
        icon = IconClipboardPlusFilled
        color = '#b520c0'
        item = items.find((item) => item.dataKey === 'history-physical')
      } else if (resource.type?.coding?.find((item) => item.code === '64290-0')) {
        icon = IconLicense
        color = '#b60e28'
        item = items.find((item) => item.dataKey === 'consents')
      } else {
        icon = IconFolderFilled
        color = '#2083c0'
        item = items.find((item) => item.dataKey === 'documents')
      }
      break
    }
    case 'RequestGroup': {
      let ext = resource.extension?.find((item) => item.url.match(/requestgroup-performer/))
      let isAuthorizedBy = false
      if (!ext) {
        ext = resource.extension?.find((item) => item.url.match(/requestgroup-authorizedBy/i))
        isAuthorizedBy = !!ext
      }
      title =
        (isAuthorizedBy ? 'Authorized by: ' : '') + ext?.valueReference?.display || 'Order Group'
      icon = IconClipboardTextFilled
      color = '#199f8b'
      item = items.find((item) => item.dataKey === 'orders')
      break
    }
    case 'DiagnosticReport': {
      const abnormalExt = resource.extension?.find((item) => item.url.match(/interpretation/))
      let prefix: string = ''
      if (
        abnormalExt?.valueCodeableConcept?.coding?.some((item) => item.display?.match(/abnormal/i))
      ) {
        prefix = '<span style="color: red">Abn </span>'
      }
      title =
        prefix +
        (resource.performer?.[0].display ||
          resource.code?.text ||
          resource.code?.coding?.[0]?.display ||
          '')
      icon = IconMicroscopeFilled
      color = '#559f19'
      item = items.find((item) => item.dataKey === 'labs-imaging')
      break
    }
    case 'Observation': {
      if (resource.category?.find((item) => item.coding?.find((i) => i.code === 'vital-signs'))) {
        title = 'Vital Signs'
        icon = IconHeartbeat
        color = '#b60e28'
        item = items.find((item) => item.dataKey === 'vitals')
      }
      if (
        resource.category?.find((item) => item.coding?.find((i) => i.code === 'social-history'))
      ) {
        title =
          resource.code?.coding?.[0]?.display ||
          resource.code?.text ||
          resource.valueString ||
          resource.valueCodeableConcept?.text ||
          resource.valueCodeableConcept?.coding?.[0]?.display ||
          resource.valueCodeableConcept?.coding?.[0]?.code ||
          'Social History'
        // @ts-expect-error - icon is custom
        icon = SocialHistoryIcon
        item = items.find((item) => item.dataKey === 'social-history')
      }
      break
    }
    case 'Coverage': {
      title = 'Insurance added'
      icon = IconShieldFilled
      color = '#d7c500'
      item = items.find((item) => item.dataKey === 'insurances')
      break
    }
    case 'Condition': {
      title = resource.code?.text || resource.code?.coding?.[0]?.display || ''
      icon = IconFileInvoiceFilled
      color = '#00c096'
      item = items.find((item) => item.dataKey === 'diagnosis')
      break
    }
    case 'MedicationStatement': {
      title =
        resource.medicationCodeableConcept?.text ||
        resource.medicationCodeableConcept?.coding?.[0]?.display ||
        resource.medicationCodeableConcept?.coding?.[0]?.code ||
        resource.contained?.find((item) => item.resourceType === 'Medication')?.code?.text ||
        'Medication added'
      icon = IconPillFilled
      color = '#e37900'
      item = items.find((item) => item.dataKey === 'medications')
      break
    }
    case 'MedicationRequest': {
      const medication = doseSpotMedicationText(resource) || 'Prescription'
      // Origin is named in the rail because a prescription and a note-authored
      // medication otherwise read identically here (§11.7 — they coexist, badged).
      title = isDoseSpotPrescription(resource) ? `${medication} (DoseSpot)` : medication
      icon = IconPillFilled
      color = '#e37900'
      item = items.find((item) => item.dataKey === 'medications')
      break
    }
    case 'AllergyIntolerance': {
      title =
        resource.code?.text || resource.code?.coding?.[0]?.display || 'NO KNOWN DRUG ALLERGIES'
      icon = IconMedicalCrossFilled
      color = '#2ae300'
      item = items.find((item) => item.dataKey === 'allergies')
      break
    }
    case 'Immunization': {
      const main = resource.vaccineCode?.text || resource.vaccineCode?.coding?.[0]?.display || ''
      const lot = resource.lotNumber ? `Lot #: ${resource.lotNumber}` : ''
      const manufacturer = resource.manufacturer?.display || resource.manufacturer?.reference || ''
      const lines = [main]
      if (lot) lines.push(lot)
      if (manufacturer) lines.push(`Manufacturer: ${manufacturer}`)
      title = `
        <div>
          ${lines
            .map(
              (l) => `
              <div>${l}</div>
            `,
            )
            .join('')}
        </div>
      `
      icon = IconVaccine
      color = '#e300c5'
      item = items.find((item) => item.dataKey === 'immunizations')
      break
    }
    case 'Procedure': {
      title = resource.code?.coding?.[0]?.display || resource.code?.text || ''
      // @ts-expect-error - icon is custom
      icon = ProcedureIcon
      item = items.find((item) => item.dataKey === 'procedures')
      break
    }
    case 'FamilyMemberHistory': {
      title =
        resource.name ||
        resource.relationship?.text ||
        resource.relationship?.coding?.[0]?.display ||
        '-'
      icon = IconFriends
      color = '#e34400'
      item = items.find((item) => item.dataKey === 'family-history')
      break
    }
    case 'Encounter': {
      const types: string[] = []

      if (resource.class.display && resource.class.display.toLowerCase() !== 'unknown') {
        const type =
          resource.class.display.charAt(0).toUpperCase() + resource.class.display.slice(1)
        types.push(type)
      }

      for (const t of resource.type || []) {
        if (t.text && !types.includes(t.text)) types.push(t.text)
      }
      title = types.join('\n') || ''
      icon = IconCalendarEvent
      color = '#00d2cb'
      item = items.find((item) => item.dataKey === 'encounters')
      break
    }
  }

  return { title, icon, color, item }
}
